import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { PhoneBookEntry, PhoneFilters } from '../types';

interface UseContactsOptions extends PhoneFilters {
  page?: number;
  pageSize?: number;
  includePrivate?: boolean;
  refresh?: number;
}

const PAGE_SIZE = 20;

export const useContacts = (options: UseContactsOptions = {}) => {
  const [contacts, setContacts] = useState<PhoneBookEntry[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchContacts = async () => {
      setLoading(true);
      setError(null);
      try {
        let query = supabase.from('phone_book').select('*', { count: 'exact' });

        if (!options.includePrivate) {
          query = query.eq('status', 'APPROVED');
        }

        if (options.name) {
          const term = `%${options.name}%`;
          query = query.or(`name.ilike.${term},marathi_name.ilike.${term}`);
        }

        if (options.shivar) query = query.eq('shivar', options.shivar);
        if (options.status && options.includePrivate) query = query.eq('status', options.status);

        const page = options.page ?? 1;
        const from = (page - 1) * PAGE_SIZE;
        const to = from + PAGE_SIZE - 1;

        const { data, error: dbError, count: total } = await query.order('marathi_name', { ascending: true }).range(from, to);

        if (dbError) throw dbError;
        setContacts((data ?? []) as PhoneBookEntry[]);
        setCount(total ?? 0);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load contacts');
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
  }, [
    options.name,
    options.shivar,
    options.status,
    options.page,
    options.includePrivate,
    options.refresh,
  ]);

  return { contacts, count, loading, error, pageSize: PAGE_SIZE };
};
